import React, { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalContext, GlobalStorage } from './GlobalContext';
import Rotas from './Rotas';
import './styles/global.css';
import ReactGA from 'react-ga';
import { CartProvider } from './CartContext';
import { ThemeProvider } from './ThemeContext';
import { isScannerEntry, isSessionExpiredEntry } from './Utills/hotelEntry';
import { getSessionExpirationMessage } from './Utills/sessionExpiration';

const ScanHotelQr = lazy(() => import('./Screens/ScanHotelQr'));

ReactGA.initialize('UA-236480625-2');
ReactGA.pageview(window.location.pathname + window.location.search);

const App = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasSessionControl = Boolean(params.get('session_control'));
  const needsSessionControl =
    location.pathname !== '/' &&
    (params.has('x') || params.has('id_cliente')) &&
    !params.get('session_control');
  const [sessionControlReady, setSessionControlReady] = React.useState(
    () => !needsSessionControl,
  );
  const sessionExpiredEntry = isSessionExpiredEntry(location.search);
  const [ultrapassouLimite, setUltrapassouLimite] = React.useState(
    () => localStorage.getItem('ultrapassou_limite') === 'true',
  );
  const [sessionExpirationMessage, setSessionExpirationMessage] = React.useState(
    getSessionExpirationMessage,
  );

  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=G-476Z82DKZ1`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-476Z82DKZ1');
  }, []);

  React.useEffect(() => {
    if (!needsSessionControl) {
      setSessionControlReady(true);
      return;
    }

    const nextParams = new URLSearchParams(location.search);
    nextParams.set('session_control', Math.floor(Date.now() / 1000).toString());
    const query = nextParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${location.pathname}?${query}${location.hash}`,
    );
    // Notifica o BrowserRouter para que todas as telas recebam a nova URL.
    window.dispatchEvent(new PopStateEvent('popstate'));
    setSessionControlReady(true);
  }, [location.hash, location.pathname, location.search, needsSessionControl]);

  React.useEffect(() => {
    const storedUltrapassouLimite = localStorage.getItem('ultrapassou_limite');
    setUltrapassouLimite(storedUltrapassouLimite === 'true');
    setSessionExpirationMessage(getSessionExpirationMessage());
  }, []);

  const radius = 8;

  if (!sessionControlReady) {
    return <p role="status">Preparando a sessão…</p>;
  }

  // Uma sessão expirada só pode continuar depois de uma nova leitura do QR Code.
  if (
    isScannerEntry(location.pathname, location.search) ||
    (ultrapassouLimite === true && !hasSessionControl)
  ) {
    return (
      <ThemeProvider>
        <Suspense fallback={<p role="status">Preparando leitor de QR Code…</p>}>
          <ScanHotelQr
            expirationMessage={sessionExpiredEntry ? sessionExpirationMessage : ''}
          />
        </Suspense>
      </ThemeProvider>
    );
  }

  // Caso contrário, exibe o app
  return (
    <ThemeProvider>
      <GlobalStorage>
        <CartProvider>
          <Rotas />
        </CartProvider>
      </GlobalStorage>
    </ThemeProvider>
  );
};

export default App;
